/**
 * Resolve the best account / IBAN / wallet identifier for checkout display & copy.
 * Never prefers masked or last-4-only strings when a full value exists on the same account.
 */

function stringifyCandidate(v) {
  if (v == null) return '';
  if (typeof v === 'bigint') return String(v);
  return String(v).trim();
}

export function looksMostlyMasked(s) {
  if (!s) return false;
  if (/\*{2,}/.test(s)) return true;
  if (/[•·‧⋯┈]{2,}/.test(s)) return true;
  const t = s.replace(/\s/g, '');
  if (/^x{2,}\d{0,8}$/i.test(t)) return true;
  if (/^\.{2,}\d+$/.test(t)) return true;
  return false;
}

function longestUnmasked(values) {
  const candidates = values.map(stringifyCandidate).filter((s) => s && !looksMostlyMasked(s));
  if (candidates.length === 0) return '';

  const digitLen = (s) => s.replace(/\D/g, '').length;
  const compactLen = (s) => s.replace(/[\s-]/g, '').length;

  return candidates.reduce((best, cur) => {
    const cb = compactLen(best);
    const cc = compactLen(cur);
    if (cc !== cb) return cc > cb ? cur : best;
    const db = digitLen(best);
    const dc = digitLen(cur);
    if (dc !== db) return dc > db ? cur : best;
    return cur.length > best.length ? cur : best;
  });
}

/**
 * Prefer the longest non-masked bank / IBAN / investment id.
 * Important: some APIs put only last-4 in `accountNumber` and the full value in
 * `fullAccountNumber` / `iban` — never return the short one when a longer field exists.
 */
export function pickVisibleAccountNumber(account, accountDetails) {
  const det = accountDetails || {};
  const a = account || {};

  const bankAndIban = longestUnmasked([
    det.fullAccountNumber,
    det.full_account_number,
    a.fullAccountNumber,
    a.full_account_number,
    det.completeAccountNumber,
    det.complete_account_number,
    a.completeAccountNumber,
    a.complete_account_number,
    det.unmaskedAccountNumber,
    det.unmasked_account_number,
    det.publicAccountNumber,
    det.public_account_number,
    det.payoutAccountNumber,
    det.payout_account_number,
    det.accountNumber,
    det.account_number,
    a.accountNumber,
    a.account_number,
    det.iban,
    det.ibanNumber,
    det.iban_number,
    a.iban,
    a.ibanNumber,
    det.investmentAccountNumber,
    det.investment_account_number,
    a.investmentAccountNumber,
    det.number,
    det.accountNo,
    det.account_no,
    a.accountNo,
    a.account_no,
  ]);

  if (bankAndIban) return bankAndIban;

  const mobileWallet = longestUnmasked([
    det.mobileNumber,
    det.mobile_number,
    det.phoneNumber,
    det.phone_number,
    det.msisdn,
    det.mobile,
    det.phone,
    det.walletNumber,
    det.wallet_number,
    a.mobileNumber,
    a.phoneNumber,
  ]);

  if (mobileWallet) return mobileWallet;

  const crypto = longestUnmasked([
    det.walletAddress,
    det.wallet_address,
    a.walletAddress,
  ]);

  if (crypto) return crypto;

  return '';
}

/** Stats for checkout / support: how much of the payout id is visible (no full value in meta). */
export function getAccountNumberDisplayMeta(accountNumber) {
  const s = stringifyCandidate(accountNumber);
  if (!s) {
    return { charLength: 0, digitCount: 0, looksMasked: false, isEmpty: true };
  }
  return {
    charLength: s.length,
    digitCount: s.replace(/\D/g, '').length,
    looksMasked: looksMostlyMasked(s),
    isEmpty: false,
  };
}
